/* eslint-disable no-restricted-globals */
import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "react-query";
import toast from "react-hot-toast";
import { useCustomToast } from "../../../../components/customtoast/CustomToastProvider";
import CustomDropdown from "../../../../components/Global/CustomDrpdown";
import { ScaleLoader } from 'react-spinners';
import { profile } from "../../../../api/profile";
import { formatDate, reverseDate } from './../../../../utils/dateFormatter';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from 'react-i18next';

const useDebounce = (fn, delay) => {
  let timeout;
  // return function that takes arg and apply to the function
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
};

function EditEducationModal({
  closeModal,
  data,
  refetchEducation,
  refetchStatus,
}) {
  const customToast = useCustomToast();
  const user = JSON.parse(sessionStorage.getItem('user'))?.user
  const [institutionSearch, setInstitutionSearch] = useState('')
  const [institutions, setInstitutions ] = useState([])
  const [searchInstitutionLoading, setSearchInstitutionLoading] = useState(false)
  const [searchCourseLoading, setSearchCourseLoading] = useState(false)
  const [courses, setCourses] = useState([]);
  const { t } = useTranslation();

  const { isLoading: institutionLoading, refetch } = useQuery(
    "get-institution",
     () => {
         return profile.getInstitutionsv2(institutionSearch)
     },
    {
      onSuccess: response => {
          setInstitutions(response?.data?.map( ins => {
             return {label: ins?.name, value: ins?.id}
          }))
      }
    }
  );
 
  const getInstitutionsMutation = useMutation(profile.getInstitutionsv2,
    {
      onSuccess: response => {
          setInstitutions(response?.data?.map( ins => {
             return {label: ins?.name, value: ins?.id}
          }))
      }
    }
  )

  const { isLoading,} = useQuery("get-courses", profile.getCoursesv2, {
    retry: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
     onSuccess: data => {
      setCourses(
        data?.data?.map((ins) => {
          return { label: ins?.title, value: ins?.id };
        })
      );
     }
  });

  const getCoursesMutation = useMutation(profile.getCoursesv2, {
    onSuccess: (response) => {
      setCourses(
        response?.data?.map((ins) => {
          return { label: ins?.title, value: ins?.id };
        })
      );
      setTimeout( () => setSearchCourseLoading(false) , 100 )
    },
  });

  const { data: degrees } = useQuery("get-degree", profile.getDegreesv2);

  const { data: classOfDegrees } = useQuery(
    "get-classOfDegrees",
    profile.getClassOfDegreesv2
  );

  const [startDate, setStartDate] = useState(reverseDate(data?.start_date));
  const [endDate, setEndDate] = useState(reverseDate(data?.end_date));

  // handle seletced states
  const [selectInstitution, setSelectedInstitution] = useState(data?.institution?.name);
  const [selectCourse, setSelectedCourse] = useState(data?.course?.title);
  const [selectDegree, setSelectedDegree] = useState(data?.degree?.name);
  const [selectClassOfDegree, setSelectedClassOfDegree] = useState(
    data?.class_of_degree?.name
  );
  const [degreeId, setDegreeId] = useState(data?.degree?.id);
  const [courseId, setCourseId] = useState(data?.course?.id);
  const [selectInstitutionId, setSelectedInstitutionId] = useState(data?.institution?.id);
  const [classOfDegreeId, setClassOfDegreeId] = useState(
    data?.class_of_degree?.id
  );

  const [institutionValid, setInstitutionValid] = useState(true);
  const [courseValid, setCourseValid] = useState(true);
  const [degreeValid, setDegreeValid] = useState(true);
  const [classofDegreeValid, setClassOfDegreeValid] = useState(true);
  const [startDateValid, setStartDateValid] = useState(true);
  const [endDateValid, setEndDateValid] = useState(true);

  const {mutateAsync, isLoading: updateEducationLoading} = useMutation(profile.updateUserEducationv2)

  const updateEducation = async (e) => {
    e.preventDefault();

    setInstitutionValid(selectInstitution !== "");
    setCourseValid(selectCourse !== "");
    setDegreeValid(selectDegree !== "");
    setClassOfDegreeValid(selectClassOfDegree !== "");
    setStartDateValid(startDate !== "");
    setEndDateValid(endDate !== "");

    if (
      !institutionValid ||
      !courseValid ||
      !degreeValid ||
      !classofDegreeValid ||
      !startDate ||
      !endDate
    ) {
      toast.error(t('edit-education.validation.all-fields'));
      return;
    }
    else if(new Date(startDate).getTime() > new Date(endDate).getTime() ){
      toast.error(t('edit-education.validation.date-error'));
      return;
    }

    let formData = {
      name: selectInstitution,
      degree: selectDegree,
      degree_id: degreeId,
      course: selectCourse,
      course_id: courseId,
      // location: selectState,
      class_of_degree: selectClassOfDegree,
      class_of_degree_id: classOfDegreeId,
      start_date: formatDate(startDate),
      end_date: formatDate(endDate),
      institution: selectInstitution,
      institution_id: selectInstitutionId
    };

    try {
      toast.loading("loading...");
      const response = await mutateAsync({ id: data.id, data: formData, userId: user?.user_id});
      toast.remove();
      customToast.success(response.message);
      await refetchEducation();
      await refetchStatus();
      closeModal(false);
    } catch ({ response }) {
      toast.remove();
      customToast.error(response?.data?.message);
    }
  };

  const deleteEducation = async (e) => {
    if (confirm(t('edit-education.delete-confirm'))) {
      try {
        const res = await profile.deleteUserEducationv2(data.id, user?.user_id);
        customToast.success(res?.message);
        await refetchEducation();
        await refetchStatus();
        closeModal(false);
      } catch ({ response }) {
        toast.remove();
        customToast.error(response?.data?.message);
      }
    }
  };

  const debouncedSearch = useDebounce((input) => {
    getInstitutionsMutation.mutate(input);
    setInstitutionSearch(input);
  }, 1500);

  const handleSearch = React.useCallback(
    (value) => {
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const courseDebouncedSearch = useDebounce((input) => {
    getCoursesMutation.mutate(input);
    setInstitutionSearch(input);
  }, 1500);

  const handleCourseSearch = React.useCallback(
    (value) => {
      courseDebouncedSearch(value);
    },
    [debouncedSearch]
  );

  useEffect(() => {
    if (getInstitutionsMutation.isLoading) {
       setSearchInstitutionLoading(true)
    } else {
      setSearchInstitutionLoading(false)
    }
  }, [getInstitutionsMutation.isLoading]);

  useEffect(() => {
    if (getCoursesMutation.isLoading) {
       setSearchCourseLoading(true)
    } else {
      setSearchCourseLoading(false)
    }
  }, [getCoursesMutation.isLoading]);

  return (
    <div className="fixed z-[1] top-0 left-0 w-[100vw] h-[100vh] bg-black bg-opacity-50">
    <div className="flex items-center justify-center h-full w-full">
      <div className="w-[96%] overflow-y-scroll z-[100000] h-[500px] md:h-[450px] md:w-[600px] bg-white rounded-[15px] p-8 md:p-8">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            {t('edit-education.title')}
          </h3>
          <div className="flex justify-between mb-4">
            <div className="text-darkBlue text-md font-medium"></div>
            <div
              className="text-[#F35B24] text-md font-medium cursor-pointer"
              onClick={deleteEducation}
            >
              {t('edit-education.remove')}
            </div>
          </div>
          
          <form onSubmit={updateEducation}>
            <div className="mb-4 relative">
            {  searchInstitutionLoading &&                 
                      <div className="absolute text-md z-[10000000000000] bg-white w-full py-3 text-center top-[70px]">
                         <div className="w-full h-full flex justify-center items-center">
                            <ScaleLoader color="#87AA4B" className="" height={10} width={10}/>
                         </div>
                      </div>
              }
              <label
                htmlFor="gender"
                className="text-gray-500 mb-4 text-base font-medium"
              >
                {t('edit-education.institution.label')}
                <span className="text-red-500 required-dot">*</span>
              </label>
              <CustomDropdown
                 value={
                   selectInstitution
                    ? { value: selectInstitution, label: selectInstitution } // Provide both value and label
                    : null
                  }
                  width={'w-full'}
                  options={
                    institutionLoading ?
                    [{label: t('edit-education.searching'), value: ''}] : institutions
                  }
                  onSearch={ (input) => {
                    setSearchInstitutionLoading(true)
                    handleSearch(input);
                   }}
                  onChange={(selectedOption) => {
                    // setSelectedInstitutionId(selectedOption?.value || "");
                    setSelectedInstitution(selectedOption?.label || "");
                    setSelectedInstitutionId(selectedOption?.value)
                    setInstitutionValid(!!selectedOption);
                  }}
                  noOptionsMessage={() => institutionLoading ? t('edit-education.searching') : t('edit-education.no-results')} 
              />
              {!institutionValid && (
                <p className="text-red-500 text-md mt-1 text-left">
                  {t('edit-education.institution.error')}
                </p>
              )}
            </div>

            <div className="mb-4 md:flex gap-x-4">
              <div className="mb-4 md:mb-0 w-full md:w-2/3 relative">
              {  searchCourseLoading &&                 
                      <div className="absolute text-md z-[10000000000000] bg-white w-full py-3 text-center top-[70px]">
                         <div className="w-full h-full flex justify-center items-center">
                            <ScaleLoader color="#87AA4B" className="" height={10} width={10}/>
                         </div>
                      </div>
                      }
                <label
                  htmlFor="gender"
                  className="text-gray-500 mb-4 text-base font-medium"
                >
                  {t('edit-education.course.label')}
                  <span className="text-red-500 required-dot">*</span>
                </label> 
                <CustomDropdown
                   options={courses}
                   value={
                    courseId
                      ? { value: courseId, label: selectCourse } // Provide both value and label
                      : null
                  }
                  width={'w-[100%]'}
                  onChange={(selectedOption) => {
                    setCourseId(selectedOption?.value || "");
                    setSelectedCourse(selectedOption?.label || "");
                    setCourseValid(!!selectedOption);
                  }}
                  onSearch={(input) => {
                    setSearchCourseLoading(true)
                    handleCourseSearch(input);
                  }}
                />
                {!courseValid && (
                  <p className="text-red-500 text-md mt-1 text-left">
                    {t('edit-education.course.error')}
                  </p>
                )}
              </div>
              <div className="mb-4 md:mb-0 w-full md:w-1/3">
                <label
                  htmlFor="degree"
                  className="text-gray-500 mb-4 text-base font-medium"
                >
                  {t('edit-education.degree.label')}
                  <span className="text-red-500 required-dot">*</span>
                </label>
                <CustomDropdown
                    options={ 
                      degrees?.data?.length ? 
                      degrees?.data?.map( degree => {
                        return { label: degree?.name , value : degree?.id }
                      } ) : []
                    }
                    value={
                      degreeId
                        ? { value: degreeId, label: selectDegree } // Provide both value and label
                        : null
                    }
                    width={'w-full'}
                    onChange={(selectedOption) => {
                      setDegreeId(selectedOption?.value || "");
                      setSelectedDegree(selectedOption?.label || "");
                      setDegreeValid(!!selectedOption);
                    }}
                />
                {!degreeValid && (
                  <p className="text-red-500 text-md mt-1 text-left">
                    {t('edit-education.degree.error')}
                  </p>
                )}
              </div>
            </div>

            <div className="mb-4 md:flex  gap-x-4">
              <div className="w-full  mb-4 md:mb-0">
                <label
                  htmlFor="class_of_degree"
                  className="text-gray-500 mb-4 text-base font-medium"
                >
                  {t('edit-education.class-of-degree.label')}
                  <span className="text-red-500 required-dot">*</span>
                </label>
                <CustomDropdown
                  options={
                    classOfDegrees?.data?.length ? 
                      classOfDegrees?.data?.map( classOfDegree => {
                        return { label: classOfDegree?.name , value : classOfDegree?.id }
                      } ) : []
                  }
                  value={
                    classOfDegreeId
                      ? { value: classOfDegreeId, label: selectClassOfDegree } // Provide both value and label
                      : null
                  }
                  width={'w-full'}
                  onChange={(selectedOption) => {
                    setClassOfDegreeId(selectedOption?.value || "");
                    setSelectedClassOfDegree(selectedOption?.label || "");
                    setClassOfDegreeValid(!!selectedOption);
                  }}
                />
                {!classofDegreeValid && (
                  <p className="text-red-500 text-md mt-1 text-left">
                    {t('edit-education.class-of-degree.error')}
                  </p>
                )}
              </div>
            </div>

            <div className="mb-8 md:flex gap-x-4">
              <div className="w-full md:w-1/2 mb-4 md:mb-0">
              <label
                    htmlFor="start_date"
                    className="text-gray-500 block mb-1 text-base font-medium"
                  >
                    {t('edit-education.dates.start.label')}
                    <span className="text-red-500 required-dot mr-2">*</span>
                 </label>
                  <DatePicker
                     selected={startDate}
                     placeholderText={t('edit-education.dates.start.placeholder')}
                     dateFormat="dd/MM/yyyy"
                     className="border border-slate-300 block focus:border-primary active:border-primary py-4 px-3 rounded-lg text-sm text-slate-700 w-full"
                     //minDate={new Date()}
                     maxDate={endDate}
                     onChange={(value) => {
                      setStartDate(value);
                      setStartDateValid(true);
                    }}
                  />
                {!startDateValid && (
                  <p className="text-red-500 text-md mt-1 text-left">
                    {t('edit-education.dates.start.error')}
                  </p>
                )}
              </div>
              <div className="w-full md:w-1/2 mb-4 md:mb-0">
                <label
                    htmlFor="end_date"
                    className="text-gray-500 mb-1 block text-base font-medium"
                  >
                    {t('edit-education.dates.end.label')}
                    <span className="text-red-500 required-dot">*</span>
                  </label>
                  <DatePicker
                     selected={endDate}
                     placeholderText={t('edit-education.dates.end.placeholder')}
                     dateFormat="dd/MM/yyyy"
                     className="border border-slate-300 block focus:border-primary active:border-primary py-4 px-3 rounded-lg text-sm text-slate-700 w-full"
                     minDate={startDate}
                    onChange={(e) => {
                      setEndDate(e);
                      setEndDateValid(true);
                    }}
                  />
                {!endDateValid && (
                  <p className="text-red-500 text-md mt-1 text-left">
                    {t('edit-education.dates.end.error')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-x-10 justify-center">
              <button
                type="button"
                className="inline-flex justify-center rounded-md border border-primary  shadow-sm px-8 py-2 md:px-16 md:py-4 bg-white text-sm font-medium text-darkBlue  sm:ml-3 sm:w-auto sm:text-sm mb-4 sm:mb-0"
                onClick={() => closeModal(false)}
              >
                {t('edit-education.buttons.cancel')}
              </button>

              <button
                type="submit"
                disabled={updateEducationLoading}
                className="inline-flex justify-center rounded-md border border-none  shadow-sm px-8 py-2 md:px-16 md:py-4 bg-primary text-sm font-medium text-white sm:ml-3 sm:w-auto sm:text-sm mb-4 sm:mb-0"
              >
                {t('edit-education.buttons.update')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditEducationModal;
